/**
 * Trunnion AI - Agent Performance Grid
 * Compact grid of agent cards showing performance metrics.
 */

import { useMemo } from 'react';
import { Bot } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { StatusDot } from '@/components/ui/StatusDot';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/config/queryKeys';
import apiClient from '@/api/client';
import type { AgentIdentity } from '@/types';

interface AgentWithMetrics extends AgentIdentity {
  successRate?: number;
  executionCount?: number;
}

// Mock metrics for agents that don't have computed metrics yet
function enrichWithMockMetrics(agents: AgentIdentity[]): AgentWithMetrics[] {
  return agents.map((agent) => ({
    ...agent,
    successRate: Math.random() * 0.4 + 0.6, // 60-100%
    executionCount: Math.floor(Math.random() * 200) + 10,
  }));
}

export function AgentPerformanceGrid() {
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.agents.lists(),
    queryFn: async () => {
      const { data } = await apiClient.get('/agents', {
        params: { page: 1, pageSize: 9, isActive: true },
      });
      return data as { agents: AgentIdentity[]; total: number };
    },
    refetchInterval: 60_000,
  });

  const agents = useMemo(() => {
    if (!data?.agents) return [];
    return enrichWithMockMetrics(data.agents).slice(0, 6);
  }, [data]);

  return (
    <Card padding="none">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            <Bot className="h-4 w-4 inline mr-1.5 text-trunnion-gold-500" />
            Agent Performance
          </CardTitle>
          <span className="text-xs text-trunnion-slate-400">
            {data?.total ?? 0} total
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSpinner size="sm" />
        ) : agents.length === 0 ? (
          <p className="text-sm text-trunnion-slate-400 text-center py-4">
            No agents configured
          </p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {agents.map((agent) => {
              const rate = agent.successRate ?? 0;
              const ratePercent = (rate * 100).toFixed(0);
              return (
                <div
                  key={agent.id}
                  className="rounded-lg border border-trunnion-slate-700/50 bg-trunnion-slate-800/50 p-3"
                >
                  {/* Agent name + status */}
                  <div className="flex items-center gap-2 mb-2">
                    <StatusDot
                      status={agent.isActive ? 'healthy' : 'idle'}
                      size="sm"
                    />
                    <span className="text-xs font-medium text-white truncate">
                      {agent.name}
                    </span>
                  </div>

                  {/* Success rate progress bar */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-[10px] mb-1">
                      <span className="text-trunnion-slate-400">Success</span>
                      <span className="text-trunnion-slate-300">{ratePercent}%</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-trunnion-slate-700">
                      <div
                        className="h-1.5 rounded-full transition-all duration-500"
                        style={{
                          width: `${ratePercent}%`,
                          backgroundColor:
                            rate >= 0.9
                              ? '#22C55E'
                              : rate >= 0.7
                                ? '#F59E0B'
                                : '#EF4444',
                        }}
                      />
                    </div>
                  </div>

                  {/* Stats row */}
                  <div className="flex items-center justify-between text-[10px]">
                    <span className="text-trunnion-slate-500">
                      {agent.executionCount?.toLocaleString()} runs
                    </span>
                    <span className="text-trunnion-slate-500 truncate max-w-20">
                      {agent.llmModelId}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
